import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import api from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import StatusBadge from "@/components/StatusBadge";
import { CheckCircle2, Circle, Truck, MapPin } from "lucide-react";

function ShipmentTimeline({ shipment }) {
  const events = shipment.events || [];
  return (
    <div className="space-y-4" data-testid="shipment-timeline">
      {events.map((e, i) => (
        <div key={i} className="flex gap-3" data-testid={`shipment-event-${i}`}>
          <div className="flex flex-col items-center">
            <div className={`w-8 h-8 rounded-full grid place-items-center ${i === events.length - 1 ? "bg-primary text-primary-foreground" : "bg-muted text-secondary"}`}>
              {i === events.length - 1 ? <CheckCircle2 className="w-4 h-4" /> : <Circle className="w-3 h-3" />}
            </div>
            {i < events.length - 1 && <div className="flex-1 w-0.5 bg-border my-1" />}
          </div>
          <div className="pb-4 flex-1">
            <div className="font-medium text-secondary capitalize">{e.status?.replace("_", " ")}</div>
            <div className="text-xs text-muted-foreground">{new Date(e.at).toLocaleString()}{e.location ? ` · ${e.location}` : ""}</div>
            {e.note && <div className="mt-1 text-sm text-muted-foreground">{e.note}</div>}
          </div>
        </div>
      ))}
    </div>
  );
}

export default function BuyerShipments() {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/shipments").then((r) => setItems(r.data)).catch(()=>{}); }, []);
  return (
    <DashboardLayout title="Shipments">
      <Card>
        <CardHeader><CardTitle className="font-heading tracking-tight">Your shipments</CardTitle></CardHeader>
        <CardContent>
          {items.length === 0 ? (
            <div className="py-10 text-center text-muted-foreground text-sm" data-testid="shipments-empty">
              <Truck className="w-8 h-8 mx-auto text-muted-foreground/60" />
              <div className="mt-2">No shipments yet.</div>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {items.map((s) => (
                <div key={s.id} className="py-4 flex items-center justify-between" data-testid={`shipment-${s.id}`}>
                  <div>
                    <div className="font-semibold text-secondary">{s.reference}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1 mt-1"><MapPin className="w-3 h-3" /> {s.origin} → {s.destination}</div>
                    <div className="text-xs text-muted-foreground mt-1">Items: {s.items}</div>
                  </div>
                  <div className="flex items-center gap-3">
                    <StatusBadge status={s.status} />
                    <Button variant="outline" size="sm" onClick={() => setOpen(s)} data-testid={`shipment-view-${s.id}`}>View timeline</Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle className="font-heading tracking-tight">Shipment {open?.reference}</DialogTitle></DialogHeader>
          {open && (<>
            <div className="text-sm text-muted-foreground mb-4">{open.origin} → {open.destination} · {open.items}</div>
            <ShipmentTimeline shipment={open} />
          </>)}
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
